import { useEffect, useRef, useState } from 'react';
import { mount } from '@multyx/embed';
import { TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * HR & Payroll — the Multyx suite embedded inside SafePass.
 *
 * SafePass owns students, gates and pickups; Multyx owns HR & Payroll. When a
 * staff member opens this page, SafePass's API mints a short-lived token for
 * them (their institution becomes the Multyx tenant), and the Multyx suite
 * renders here — no second login.
 *
 * This page deliberately does NOT use PageLayout. The embed is another product's
 * full interface, with its own header and navigation, so wrapping it in our
 * breadcrumb and title card would put two page headers on screen. It gets the
 * bare canvas and fills it.
 */

const MULTYX_ORIGIN = import.meta.env.VITE_MULTYX_ORIGIN ?? 'http://localhost:5178';
const MULTYX_CLIENT_ID = import.meta.env.VITE_MULTYX_CLIENT_ID ?? 'safepass';

export function HRPayrollPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const embed = mount({
      container: containerRef.current,
      origin: MULTYX_ORIGIN,
      clientId: MULTYX_CLIENT_ID,
      path: '/',
      height: '100%',
      // Mints a FRESH token per call, from SafePass's own API. The empty object
      // body is deliberate: SafePass's API client always sends a JSON
      // content-type, and Fastify rejects that with an empty body.
      getToken: async () => {
        const { token } = await api.post<{ token: string }>('/multyx/token', {});
        return token;
      },
      onError: (e) => setError(e.message),
    });

    return () => embed.destroy();
  }, []);

  return (
    <div className="flex h-[calc(100vh-theme(spacing.bar)-1.75rem)] flex-col overflow-clip rounded-lg border border-border bg-card">
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 border-b border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
        >
          <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
          HR &amp; Payroll could not load: {error}
        </div>
      )}
      <div ref={containerRef} className="flex-1" />
    </div>
  );
}
